import { User, AuthState } from '../models/types';
import ApiService from './ApiService';
import SecureStorage from './SecureStorage';
import { storage, STORAGE_KEYS } from './Storage';

class AuthService {
    private initialized = false;

    /**
     * Initialize the auth service
     */
    async init(): Promise<void> {
        if (this.initialized) return;
        await ApiService.init();
        this.initialized = true;
    }

    /**
     * Login with username and password
     */
    async login(username: string, password: string): Promise<{ success: boolean; user?: User; token?: string; error?: string }> {
        try {
            await this.init();

            const result = await ApiService.login(username, password);

            // Store session locally
            this.saveSession(result.user, result.token);

            // Store credentials for auto-login
            SecureStorage.saveCredentials(username, password);

            return {
                success: true,
                user: result.user,
                token: result.token,
            };
        } catch (error: any) {
            return { success: false, error: error.message || 'An error occurred during login' };
        }
    }

    /**
     * Auto-login using stored credentials
     * This makes a fresh API call to validate credentials
     */
    async autoLogin(): Promise<{ success: boolean; user?: User; token?: string; error?: string }> {
        try {
            await this.init();

            // Get stored credentials
            const credentials = SecureStorage.getCredentials();

            if (!credentials) {
                return { success: false, error: 'No stored credentials' };
            }

            // console.log('Auto-login: Found stored credentials, attempting login...');

            // Perform fresh login with stored credentials
            // This ensures we always have a valid session from the server
            const result = await ApiService.login(credentials.username, credentials.password);

            // Update stored session
            this.saveSession(result.user, result.token);

            // Refresh credentials storage (optional, but good practice in case encoding changes)
            SecureStorage.saveCredentials(credentials.username, credentials.password);

            return {
                success: true,
                user: result.user,
                token: result.token,
            };
        } catch (error: any) {
            console.error('Auto-login failed:', error);
            // If login fails (e.g. password changed), clear stored credentials
            if (error.statusCode === 401 || error.statusCode === 403) {
                SecureStorage.clearCredentials();
            }
            return { success: false, error: error.message || 'Auto-login failed' };
        }
    }

    /**
     * Logout and clear session
     */
    async logout(): Promise<void> {
        try {
            await ApiService.logout();
            storage.delete(STORAGE_KEYS.TOKEN);
            storage.delete(STORAGE_KEYS.USER);
            SecureStorage.clearCredentials();
        } catch (error) {
            console.error('Logout error:', error);
        }
    }

    /**
     * Save session to Storage
     */
    private saveSession(user: User, token: string): void {
        try {
            const { password: _, ...userWithoutPassword } = user as any;
            storage.set(STORAGE_KEYS.TOKEN, token);
            storage.set(STORAGE_KEYS.USER, JSON.stringify(userWithoutPassword));
        } catch (error) {
            console.error('Save session error:', error);
            throw error;
        }
    }

    /**
     * Get current session
     */
    async getSession(): Promise<AuthState> {
        try {
            await this.init();

            const token = storage.getString(STORAGE_KEYS.TOKEN);
            const userStr = storage.getString(STORAGE_KEYS.USER);

            if (token && userStr) {
                const user = JSON.parse(userStr) as User;
                return {
                    isAuthenticated: true,
                    user,
                    token,
                };
            }

            return {
                isAuthenticated: false,
                user: null,
                token: null,
            };
        } catch (error) {
            console.error('Get session error:', error);
            return {
                isAuthenticated: false,
                user: null,
                token: null,
            };
        }
    }

    /**
     * Check if user is authenticated
     */
    async isAuthenticated(): Promise<boolean> {
        const session = await this.getSession();
        return session.isAuthenticated;
    }

    /**
     * Get current user
     */
    async getCurrentUser(): Promise<User | null> {
        const session = await this.getSession();
        return session.user;
    }

    /**
     * Reset password (using API)
     */
    async resetPassword(username: string, newPassword: string): Promise<{ success: boolean; error?: string }> {
        try {
            await ApiService.updatePassword(newPassword);
            return { success: true };
        } catch (error: any) {
            console.error('Reset password error:', error);
            return { success: false, error: error.message || 'Failed to reset password' };
        }
    }
}

export default new AuthService();
